import React, { FC, useState, useMemo } from 'react'
import { Container, Radio, Typography } from '@cleartrip/ct-design-components'
import CFWHomePageCallout from '@/components/home/CFW/HomePageCallout'
import SplFareType from '@/components/home/SpecialFare'
import { FareTypeInterface, REVAMP_FARE_CHIP_CLASS } from '@/constants/components/fareType'
import CfwErrorModal from '@/components/home/CFW/CfwErrorModal'
import StudentFareIntlModal from '@/components/home/StudentFareIntlModal'
import dynamic from 'next/dynamic'
import { isServer } from '@/utils'
import { EMPTY_BLOCK_FUNCTION } from '@/constants/pages/common'
import { Raven } from '@/analytics/raven'
import { RAVEN_EVENT_NAME, RAVEN_PAGE_NAME } from '@/analytics/raven/ravenConstants'

const CfwModalSSr = dynamic(import('@/components/home/CFW/HomePageCallout/CfwModal'), {
    ssr: false,
    loading: () => <></>
})

interface SpecialFaresRowProps {
    isIntl: boolean
    activeFare: string
    setActiveFare: React.Dispatch<React.SetStateAction<string>>
    setIsCfwSelected: React.Dispatch<React.SetStateAction<boolean>>
    isCfwSelected: boolean
    cfwModal: boolean
    setCfwModal: (value: boolean) => void
    fareTypeList: FareTypeInterface[]
    tripType: string
    variant?: 'home' | 'srp'
    searchHandler?: () => void
    isThemed?: boolean
    /** First Class hero — the label sits on a near-black background. */
    isDarkThemed?: boolean
    isRevamp?: boolean
}

const SpecialFaresRow: FC<SpecialFaresRowProps> = ({
    isIntl,
    activeFare,
    setActiveFare,
    setIsCfwSelected,
    isCfwSelected,
    cfwModal,
    setCfwModal,
    fareTypeList,
    tripType,
    variant = 'home',
    searchHandler,
    isThemed = false,
    isDarkThemed = false,
    isRevamp = false
}) => {
    const [cfwErrorModal, setCfwErrorModal] = useState('')
    const [cfwErrorModalButtonText, setCfwErrorModalButtonText] = useState('Okay, got it')
    const [showStudentFareIntlModal, setShowStudentFareIntlModal] = useState(false)

    const fareList = useMemo(() => {
        if (isIntl) {
            return fareTypeList.filter(fare => fare.code === 'student')
        }
        return fareTypeList
    }, [isIntl, fareTypeList])

    // CFW is now supported for international flights too, so we no longer reset
    // the selection or show the "domestic only" error when the sector is intl.

    const handleCfwModalClose = (value: boolean) => {
        setCfwModal(value)
        // if(!value && !isUserSignedIn()) {
        //     setIsCfwSelected(false)
        // }
    }

    const setActiveFareHandler = (fareType: string) => {
        // Revamp chips are a radio group: re-clicking the selected fare is a no-op.
        if (isRevamp && activeFare === fareType) return

        if (isIntl) {
            if (fareType === 'student') {
                if (activeFare !== fareType) {
                    setShowStudentFareIntlModal(true)
                }
            }
        }

        if (isCfwSelected) {
            if (fareType === 'senior_citizen') {
                setCfwErrorModal('Senior citizen fare does not include the benefits of Cleartrip for work')
                setCfwErrorModalButtonText('Switch to Senior citizen fare')
            } else if (fareType === 'student') {
                setCfwErrorModal('Student fare does not include the benefits of Cleartrip for work')
                setCfwErrorModalButtonText('Switch to Student fare')
            } else if (fareType === 'army') {
                setCfwErrorModal('Armed forces fare does not include the benefits of Cleartrip for work')
                setCfwErrorModalButtonText('Switch to Armed forces fare')
            }
            return
        }

        if (activeFare === fareType) {
            setActiveFare('')
            return
        }
        setActiveFare(fareType)
    }

    const handleStudentFareIntlConfirm = () => {
        setShowStudentFareIntlModal(false)
        setActiveFare('student')
    }

    const renderPipeSymbol = () => {
        return (
            <div
                style={{
                    width: 1,
                    height: variant === 'srp' || isIntl ? 30 : 36,
                    backgroundColor: '#E6E6E6',
                    flexShrink: 0
                }}
            />
        )
    }

    const onRegularClick = () => {
        Raven.push({
            eventName: RAVEN_EVENT_NAME.HOME_PTC_FARE_SELECTED,
            eventData: {
                action_name: RAVEN_EVENT_NAME.HOME_PTC_FARE_SELECTED,
                page_name: RAVEN_PAGE_NAME.FLIGHTS_HOME,
                a_fare_type: 'regular',
                action_type: 'click',
            },
        })
        setIsCfwSelected(false)
        setActiveFare('')
    }

    const legacyRowGapClass = variant === 'srp' || isIntl ? 'flex-cg-4' : 'flex-cg-5'
    const rowGapClass = isRevamp ? 'flex-cg-2' : legacyRowGapClass
    const fareRowClassName = `flex flex-row flex-middle ${rowGapClass}`
    const fareTypesLabelColor = isDarkThemed ? 'neutral' : 'subheading'

    return (
        <>
            <Container className={fareRowClassName}>
                {isRevamp && (
                    <>
                        <Typography color={fareTypesLabelColor} variant="B2">
                            Fare types:
                        </Typography>
                        <Container className={REVAMP_FARE_CHIP_CLASS} onClick={onRegularClick}>
                            <Radio
                                checked={!activeFare && !isCfwSelected}
                                id="fare-regular"
                                name="srpFareType"
                                value="regular"
                                onChange={EMPTY_BLOCK_FUNCTION}
                            />
                            <Typography variant="B3">Regular</Typography>
                        </Container>
                    </>
                )}
                <CFWHomePageCallout
                    isIntl={isIntl}
                    setCfwErrorModal={setCfwErrorModal}
                    setCfwErrorModalButtonText={setCfwErrorModalButtonText}
                    activeFare={activeFare}
                    setIsCfwSelected={setIsCfwSelected}
                    isCfwSelected={isCfwSelected}
                    setCfwModal={setCfwModal}
                    variant={variant}
                    isRevamp={isRevamp}
                    isThemed={isThemed}
                />
                {!isRevamp && renderPipeSymbol()}
                {(variant === 'srp' && !isIntl && !isRevamp) && (
                    <Container className="flex flex-column" style={{ flexShrink: 0 }}>
                        <Typography
                            variant="B3"
                            style={{ color: isDarkThemed ? '#FFFFFF' : '#808080', fontSize: '12px', lineHeight: '16px', width: '45px' }}
                        >
                            Special fares
                        </Typography>
                    </Container>
                )}
                <SplFareType
                    activeFare={activeFare}
                    fareTypeList={fareList}
                    handleClick={setActiveFareHandler}
                    tripType={tripType}
                    variant={variant}
                    isIntl={isIntl}
                    isRevamp={isRevamp}
                    isThemed={isThemed}
                />
            </Container>
            
            <CfwErrorModal
                cfwErrorModal={cfwErrorModal}
                cfwErrorModalButtonText={cfwErrorModalButtonText}
                isCfwSelected={isCfwSelected}
                setActiveFareHandler={setActiveFareHandler}
                setIsCfwSelected={setIsCfwSelected}
                isIntl={isIntl}
                setCfwErrorModal={setCfwErrorModal}
                setCfwErrorModalButtonText={setCfwErrorModalButtonText}
                setActiveFare={setActiveFare}
            />
            {!isServer() && (
                <StudentFareIntlModal
                    open={showStudentFareIntlModal}
                    onClose={handleStudentFareIntlConfirm}
                    onConfirm={handleStudentFareIntlConfirm}
                />
            )}

            {!isServer() && searchHandler && (
                <CfwModalSSr
                    cfwModal={cfwModal}
                    setCfwModal={handleCfwModalClose}
                    searchHandler={searchHandler}
                />
            )}
        </>
    )
}

export default SpecialFaresRow
